/**
 * Master Key generation and wrapping.
 * 
 * Architecture:
 * - Master key is randomly generated at signup (never derived from password)
 * - Master key is "wrapped" (encrypted) by multiple keys for different access paths:
 *   - wrapped_by_password: encrypted with password-derived key (for normal login)
 *   - wrapped_by_recovery: encrypted with recovery key (for password reset)
 *   - device storage: encrypted with device key (for session persistence)
 * 
 * This decouples password from encryption:
 * - Password change only requires re-wrapping, not re-encrypting all data
 * - Recovery key can decrypt master key even after password is forgotten
 */

import { encrypt, decrypt } from './encryption';

/**
 * Generate a random master key.
 * 
 * @returns 32-byte random master key
 */
export function generateMasterKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32));
}

/**
 * Derive a password key from password and email.
 * Uses PBKDF2 with SHA-256 for consistent cross-browser support.
 * 
 * Note: This is different from Argon2id used for authKey derivation.
 * We use Web Crypto API here for wrapping the master key.
 * 
 * @param password - User's password
 * @param email - User's email (used as salt component)
 * @returns 32-byte password-derived key
 */
export async function derivePasswordKey(password: string, email: string): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  
  // Import password as key material
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveBits']
  );
  
  // Derive 256 bits using PBKDF2
  const salt = encoder.encode(`consl-password-wrap-${email.toLowerCase().trim()}`);
  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    256
  );
  
  return new Uint8Array(derivedBits);
}

/**
 * Wrap (encrypt) master key with password-derived key.
 * 
 * @param masterKey - The master encryption key
 * @param passwordKey - Key derived from password via derivePasswordKey()
 * @returns Wrapped master key as base64 string
 */
export async function wrapMasterKeyWithPassword(
  masterKey: Uint8Array,
  passwordKey: Uint8Array
): Promise<string> {
  const masterKeyBase64 = btoa(String.fromCharCode(...masterKey));
  return encrypt(masterKeyBase64, passwordKey);
}

/**
 * Unwrap (decrypt) master key with password-derived key.
 * 
 * @param wrappedMasterKey - Wrapped master key from server
 * @param passwordKey - Key derived from password via derivePasswordKey()
 * @returns Unwrapped master key
 */
export async function unwrapMasterKeyWithPassword(
  wrappedMasterKey: string,
  passwordKey: Uint8Array
): Promise<Uint8Array> {
  const masterKeyBase64 = await decrypt(wrappedMasterKey, passwordKey);
  return Uint8Array.from(atob(masterKeyBase64), c => c.charCodeAt(0));
}

/**
 * Derive encryption key from recovery key display string.
 * Used for both wrap and unwrap - the display string is the source of truth.
 * 
 * @param recoveryKeyStr - Recovery key in display format (XXXX-XXXX-...) or normalized
 * @returns 32-byte encryption key
 */
async function deriveRecoveryEncryptionKey(recoveryKeyStr: string): Promise<Uint8Array> {
  const normalized = recoveryKeyStr.replace(/-/g, '').toUpperCase();
  const encoder = new TextEncoder();
  const data = encoder.encode(`consl-recovery-key-${normalized}`);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(hashBuffer);
}

/**
 * Wrap (encrypt) master key with recovery key.
 * 
 * @param masterKey - The master encryption key
 * @param recoveryKeyDisplayStr - Recovery key in display format (XXXX-XXXX-...)
 * @returns Wrapped master key as base64 string
 */
export async function wrapMasterKeyWithRecovery(
  masterKey: Uint8Array,
  recoveryKeyDisplayStr: string
): Promise<string> {
  const encryptionKey = await deriveRecoveryEncryptionKey(recoveryKeyDisplayStr);
  const masterKeyBase64 = btoa(String.fromCharCode(...masterKey));
  return encrypt(masterKeyBase64, encryptionKey);
}

/**
 * Unwrap (decrypt) master key with recovery key string.
 * 
 * @param wrappedMasterKey - Wrapped master key from server
 * @param recoveryKeyStr - Recovery key string entered by user (XXXX-XXXX-...)
 * @returns Unwrapped master key
 */
export async function unwrapMasterKeyWithRecovery(
  wrappedMasterKey: string,
  recoveryKeyStr: string
): Promise<Uint8Array> {
  const encryptionKey = await deriveRecoveryEncryptionKey(recoveryKeyStr);
  const masterKeyBase64 = await decrypt(wrappedMasterKey, encryptionKey);
  return Uint8Array.from(atob(masterKeyBase64), c => c.charCodeAt(0));
}
